'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type NavLink = {
  key: string
  label: string
  targetId?: string
  href?: string
  primary?: boolean
}

const NAVIGATION_LINKS: NavLink[] = [
  { key: 'leagues', label: 'LEAGUES', targetId: 'landing-leagues', primary: true },
  { key: 'about', label: 'ABOUT', targetId: 'landing-about' },
  { key: 'how-to', label: 'HOW TO', targetId: 'landing-how-to' },
  { key: 'partners', label: 'SPONSORS & PARTNERS', targetId: 'landing-sponsors' },
  { key: 'contact', label: 'CONTACT US', href: '/contact' }
]

const SCROLL_DEBOUNCE_MS = 100

export default function LandingNavigation() {
  const pathname = usePathname()
  const router = useRouter()
  const [activeLink, setActiveLink] = React.useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const sectionLinksRef = React.useRef(NAVIGATION_LINKS.filter((link) => link.targetId))

  const scrollToSection = React.useCallback((targetId: string) => {
    const section = document.getElementById(targetId)
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const handleNavClick = React.useCallback(
    (targetId: string, key: string) => {
      setActiveLink(key)
      if (pathname === '/') {
        scrollToSection(targetId)
      } else {
        router.push(`/#${targetId}`)
      }
    },
    [scrollToSection, pathname, router]
  )

  const handleDrawerNavClick = React.useCallback(
    (targetId: string, key: string) => {
      setIsDrawerOpen(false)
      handleNavClick(targetId, key)
    },
    [handleNavClick]
  )

  // Lock body scroll when drawer is open
  React.useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isDrawerOpen])

  // Set active link based on pathname
  React.useEffect(() => {
    if (pathname === '/contact') {
      setActiveLink('contact')
    } else if (pathname !== '/') {
      setActiveLink(null)
    }
  }, [pathname])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  // Scroll-based section detection using position math
  React.useEffect(() => {
    if (pathname !== '/') return

    const handleScroll = () => {
      // Clear existing debounce timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      scrollTimeoutRef.current = setTimeout(() => {
        // Get viewport position (using center of viewport as reference)
        const viewportCenter = window.scrollY + window.innerHeight / 2

        // Find which section's bounding box contains the viewport center
        let closestLink: string | null = null
        let closestDistance = Infinity

        sectionLinksRef.current.forEach((link) => {
          const section = document.getElementById(link.targetId!)
          if (!section) return

          const rect = section.getBoundingClientRect()
          const sectionTop = window.scrollY + rect.top
          const sectionBottom = sectionTop + rect.height

          // Check if viewport center is within this section
          if (viewportCenter >= sectionTop && viewportCenter <= sectionBottom) {
            closestLink = link.key
            closestDistance = 0
            return
          }

          // If not within section, find closest section edge
          const distanceToTop = Math.abs(viewportCenter - sectionTop)
          const distanceToBottom = Math.abs(viewportCenter - sectionBottom)
          const distance = Math.min(distanceToTop, distanceToBottom)

          if (distance < closestDistance) {
            closestDistance = distance
            closestLink = link.key
          }
        })

        if (closestLink) {
          setActiveLink(closestLink)
        }
      }, SCROLL_DEBOUNCE_MS)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-black/30 bg-neu-surface shadow-[0_15px_35px_rgba(5,5,5,0.65)]">
        <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between gap-8 pl-0 pr-4 py-3 sm:pr-6 lg:pr-10">
          {/* Hamburger button - visible below lg */}
          <button
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            className="flex items-center justify-center w-10 h-10 text-white lg:hidden ml-2"
            aria-label="Open navigation menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link
            href="/"
            aria-label="NeuroPlay Fantasy Championship"
            className="flex items-center pl-2 lg:pl-2"
          >
            <Image
              src="/assets/Logos/NPFCBlackLogo.png"
              alt="NPFC"
              width={180}
              height={48}
              className="h-10 sm:h-12 block dark:hidden"
              style={{ width: 'auto' }}
              priority
            />
            <Image
              src="/assets/Logos/NPFCwhiteLogo.png"
              alt="NPFC"
              width={180}
              height={48}
              className="h-10 sm:h-12 hidden dark:block"
              style={{ width: 'auto' }}
              priority
            />
          </Link>

          {/* Spacer to keep logo centered on mobile */}
          <div className="w-10 lg:hidden" />

          <nav className="hidden flex-1 items-center justify-center gap-6 text-[0.75rem] font-semibold uppercase tracking-[0.25em] text-white md:gap-10 lg:flex">
            {NAVIGATION_LINKS.map((link) => {
              const isActive = activeLink === link.key

              const sharedClasses = cn(
                'flex items-center justify-center rounded-lg px-5 py-2 transition-all duration-300',
                isActive
                  ? 'bg-gradient-to-r from-neu-brand-from to-neu-brand-to text-white shadow-[0_10px_25px_rgba(7,113,185,0.45)]'
                  : 'text-white/80 hover:text-white'
              )

              if (link.targetId) {
                return (
                  <button
                    key={link.key}
                    type="button"
                    onClick={() => handleNavClick(link.targetId!, link.key)}
                    className={sharedClasses}
                  >
                    {link.label}
                  </button>
                )
              }

              if (link.href) {
                return (
                  <Link
                    key={link.key}
                    href={link.href}
                    onClick={() => setActiveLink(link.key)}
                    className={sharedClasses}
                  >
                    {link.label}
                  </Link>
                )
              }

              return null
            })}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Link
              href="/login"
              className="rounded-lg bg-neu-surface-recessed px-6 py-2 text-xs font-bold uppercase tracking-[0.3em] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] transition hover:bg-neu-surface-hover"
            >
              LOGIN
            </Link>
            <Link
              href="/register"
              className="rounded-lg bg-gradient-to-r from-neu-brand-from to-neu-brand-to px-6 py-2 text-xs font-bold uppercase tracking-[0.3em] text-white shadow-[0px_14px_25px_rgba(7,113,185,0.35)] transition hover:brightness-110"
            >
              REGISTER
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setIsDrawerOpen(false)}
          aria-hidden
        />
      )}

      {/* Mobile Drawer Panel */}
      <div
        className={cn(
          'fixed top-0 left-0 z-[70] h-full w-[280px] bg-neu-surface shadow-[10px_0_40px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-in-out lg:hidden',
          isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <Image
            src="/assets/Logos/NPFCBlackLogo.png"
            alt="NPFC"
            width={120}
            height={36}
            className="block dark:hidden"
            style={{ width: 'auto', height: '2rem' }}
          />
          <Image
            src="/assets/Logos/NPFCwhiteLogo.png"
            alt="NPFC"
            width={120}
            height={36}
            className="hidden dark:block"
            style={{ width: 'auto', height: '2rem' }}
          />
          <button
            type="button"
            onClick={() => setIsDrawerOpen(false)}
            className="flex items-center justify-center w-9 h-9 text-white/80 hover:text-white"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Nav Links */}
        <nav className="flex flex-col py-4">
          {NAVIGATION_LINKS.map((link) => {
            const isActive = activeLink === link.key
            const itemClasses = cn(
              'px-6 py-3.5 text-[0.8rem] font-semibold uppercase tracking-[0.2em] transition-colors duration-200',
              isActive
                ? 'text-white bg-gradient-to-r from-neu-brand-from/20 to-neu-brand-to/20 border-l-2 border-[#2991d5]'
                : 'text-white/70 hover:text-white hover:bg-white/5'
            )

            if (link.targetId) {
              return (
                <button
                  key={link.key}
                  type="button"
                  onClick={() => handleDrawerNavClick(link.targetId!, link.key)}
                  className={cn(itemClasses, 'text-left')}
                >
                  {link.label}
                </button>
              )
            }

            if (link.href) {
              return (
                <Link
                  key={link.key}
                  href={link.href}
                  onClick={() => {
                    setActiveLink(link.key)
                    setIsDrawerOpen(false)
                  }}
                  className={itemClasses}
                >
                  {link.label}
                </Link>
              )
            }

            return null
          })}
        </nav>

        {/* Drawer Footer - Login/Register */}
        <div className="absolute bottom-0 left-0 right-0 px-5 py-6 border-t border-white/10 space-y-3">
          <Link
            href="/login"
            onClick={() => setIsDrawerOpen(false)}
            className="block w-full text-center rounded-lg bg-neu-surface-recessed px-4 py-2.5 text-xs font-bold uppercase tracking-[0.3em] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] transition hover:bg-neu-surface-hover"
          >
            LOGIN
          </Link>
          <Link
            href="/register"
            onClick={() => setIsDrawerOpen(false)}
            className="block w-full text-center rounded-lg bg-gradient-to-r from-neu-brand-from to-neu-brand-to px-4 py-2.5 text-xs font-bold uppercase tracking-[0.3em] text-white shadow-[0px_14px_25px_rgba(7,113,185,0.35)] transition hover:brightness-110"
          >
            REGISTER
          </Link>
        </div>
      </div>
    </>
  )
}
